import { useCallback, useEffect, useRef, useState } from 'react';
import { AxiosError } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { useAuthStore } from '../store/authStore';
import { authService } from '../services/authService';
import { User } from '../types/auth.types';
import { handleApiError } from '@/lib/utils/errorHandler';

const RESEND_INTERVAL_SECONDS = 60;

export type LoginOtpStep = 'email' | 'otp';

export const useLoginOtp = () => {
  const [step, setStep] = useState<LoginOtpStep>('email');
  const [userId, setUserId] = useState('');
  const [uniqueCode, setUniqueCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [resendSecondsLeft, setResendSecondsLeft] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sessionRef = useRef({ userId: '', uniqueCode: '' });
  const { login: setAuthState } = useAuthStore();

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startResendTimer = useCallback(
    (seconds: number = RESEND_INTERVAL_SECONDS) => {
      clearTimer();
      setResendSecondsLeft(seconds);
      timerRef.current = setInterval(() => {
        setResendSecondsLeft((prev) => {
          if (prev <= 1) {
            clearTimer();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    },
    [clearTimer]
  );

  useEffect(() => {
    return () => clearTimer();
  }, [clearTimer]);

  const sendOtp = async (email: string): Promise<boolean> => {
    setSending(true);
    setError(null);
    setMessage(null);

    const code = uuidv4();

    try {
      const response = await authService.sendOtp({
        userId: email,
        uniqueCode: code,
      });
      sessionRef.current = { userId: email, uniqueCode: code };
      setUserId(email);
      setUniqueCode(code);
      setMessage(response.message);
      setStep('otp');
      startResendTimer();
      return true;
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError.message);
      return false;
    } finally {
      setSending(false);
    }
  };

  const resendOtp = async (): Promise<boolean> => {
    const { userId: sessionUserId, uniqueCode: sessionUniqueCode } =
      sessionRef.current;
    if (!sessionUserId || !sessionUniqueCode || resendSecondsLeft > 0) {
      return false;
    }

    setSending(true);
    setError(null);
    setMessage(null);

    try {
      const response = await authService.resendOtp({
        userId: sessionUserId,
        uniqueCode: sessionUniqueCode,
      });
      setMessage(response.message);
      startResendTimer();
      return true;
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 429) {
        const apiError = handleApiError(err);
        setError(apiError.message);
        startResendTimer();
        return false;
      }
      const apiError = handleApiError(err);
      setError(apiError.message);
      return false;
    } finally {
      setSending(false);
    }
  };

  const verifyOtp = async (otp: string): Promise<User | null> => {
    const { userId: sessionUserId, uniqueCode: sessionUniqueCode } =
      sessionRef.current;
    if (!sessionUserId || !sessionUniqueCode) {
      return null;
    }

    setVerifying(true);
    setError(null);

    try {
      const user = await authService.verifyOtp({
        userId: sessionUserId,
        uniqueCode: sessionUniqueCode,
        otp,
      });
      setAuthState(user, '');
      return user;
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError.message);
      return null;
    } finally {
      setVerifying(false);
    }
  };

  const reset = () => {
    clearTimer();
    sessionRef.current = { userId: '', uniqueCode: '' };
    setStep('email');
    setUserId('');
    setUniqueCode('');
    setError(null);
    setMessage(null);
    setResendSecondsLeft(0);
  };

  return {
    step,
    userId,
    sending,
    verifying,
    loading: sending || verifying,
    error,
    message,
    resendSecondsLeft,
    canResend: resendSecondsLeft === 0 && step === 'otp',
    sendOtp,
    resendOtp,
    verifyOtp,
    reset,
  };
};
